import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Typography, Box, Grid2 as Grid, Link } from '@mui/material';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

import { db } from '../firebase';
import { Context2 } from './Context2';
import { useI18n } from '../i18n/I18nContext';
import { CLOCK_TICK_MS, deriveStatus, isLive, lineSortKey, serviceDays } from './deriveJourney';
import { toMs } from '../utils/liveTime';
import type { JourneyDoc, JourneyStatus } from './types';

/**
 * ONE listener on the flat `live_journeys` collection, scoped to the current
 * service days.
 *
 * The scope matters. RIS:Journeys carries timetable data up to 48 hours ahead and
 * nothing deletes old documents (the janitor sweep does not exist yet), so an
 * unscoped listener downloads the entire collection — thousands of documents at
 * ~6 KB each. Filtering client-side would hide them but still pay for them.
 * `date_cal` is an indexed top-level string, so this needs no composite index.
 *
 * No occupancy listener. Subscribing to live_journey_occupancy for the whole board
 * would be ~150 documents at up to 48 KB for a 3px bar; occupancy is read only for
 * the selected journey, in LiveView2.
 */

type Row = {
  journeyId: string;
  routeId: string | null;
  lineCode: string;
  status: JourneyStatus;
  expiresMs: number | null;
  sourceUpdatedMs: number | null;
  schedDepMs: number | null;
};

const STATUS_COLOUR: Record<JourneyStatus, string> = {
  ACTIVE: '#ffff54',      // reported moving
  ARRIVED: '#8fb0c7',     // finished
  SCHEDULED: '#ffffff',   // due shortly, nothing observed yet
  NO_DATA: '#c78f8f',     // past departure, nothing observed — a data gap
  CANCELLED: '#8a8a8a',
};

const BLINK_MS = 30000;

/**
 * Legend order: the journey's own lifecycle, not the object key order.
 * SCHEDULED -> ACTIVE -> ARRIVED is what a train does; NO_DATA and CANCELLED
 * are the two ways it leaves that path.
 */
const LEGEND_ORDER: JourneyStatus[] = [
  'SCHEDULED', 'ACTIVE', 'ARRIVED', 'NO_DATA', 'CANCELLED',
];

/**
 * The colour key for the board.
 *
 * Until now the five status colours and the blink were the only thing carrying
 * state, with the status name reachable solely through a `title` attribute —
 * hover-only, and invisible on touch. Colour was therefore the sole encoding of
 * meaning, which is the one thing a status palette must never be.
 *
 * Two deliberate details:
 *
 * - LABELS WEAR THE SIDEBAR'S INK, NOT THE STATUS COLOUR. The swatch carries
 *   identity; colouring the text too would make CANCELLED (#8a8a8a, 3.65:1
 *   against this surface) illegible at 9px in the very place that explains it.
 *
 * - THE ORANGE IS LISTED SEPARATELY, because it is not a status. It means the
 *   document was written within BLINK_MS and it OVERRIDES whatever status the
 *   journey has, so a key that folded it in with the other five would be
 *   describing a sixth state that does not exist.
 */
const Legend: React.FC = () => {
  const { t } = useI18n();
  const swatch = (colour: string) => (
    <Box component="span" sx={{
      width: 8, height: 8, borderRadius: '50%',
      backgroundColor: colour, flexShrink: 0,
    }} />
  );
  const row = (colour: string, label: string) => (
    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
      {swatch(colour)}
      <Typography sx={{ fontSize: '9px', color: '#98a1aa' }} noWrap>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{
      // Sticky, because a key below a long list of trains is a key nobody
      // finds. Same surface as the header so it reads as chrome, not content.
      position: 'sticky', bottom: 0, zIndex: 2,
      bgcolor: '#18364a', borderTop: '1px solid rgba(255,255,255,0.08)',
      px: '10px', pt: '6px', pb: '8px', mt: '8px',
    }}>
      <Typography sx={{
        fontSize: '8.5px', color: '#5d6679', letterSpacing: '0.08em',
        textTransform: 'uppercase', mb: '4px',
      }} noWrap>
        {t('sidebar.legend')}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 6px' }}>
        {LEGEND_ORDER.map(st => row(STATUS_COLOUR[st], t(`status.${st}`)))}
      </Box>

      <Box sx={{ mt: '5px', pt: '5px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {row('#FFA500', t('sidebar.recent'))}
      </Box>
    </Box>
  );
};

const Sidebar2: React.FC = () => {
  const { selected, setSelected } = useContext(Context2);
  const { t } = useI18n();
  const [docs, setDocs] = useState<JourneyDoc[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Status and the live window are functions of wall-clock time, so no Firestore
  // event fires when they change. Without this tick the board freezes between
  // writes and trains never age off.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Only changes at midnight Berlin time, so the listener is not torn down on
  // every tick.
  const days = useMemo(() => serviceDays(nowMs), [nowMs]);
  const daysKey = days.join(',');

  useEffect(() => {
    const q = query(collection(db, 'live_journeys'), where('date_cal', 'in', daysKey.split(',')));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setError(null);
        setDocs(snap.docs.map((d) => ({ ...(d.data() as JourneyDoc), journey_id: d.id })));
      },
      (err) => {
        // Most likely cause on first run is missing security rules for the new
        // collection — the default deny at the end of firestore.rules.
        console.error('❌ live_journeys onSnapshot error', err);
        setError(err.message);
      }
    );
    return () => unsub();
  }, [daysKey]);

  const rows: Row[] = useMemo(
    () =>
      docs
        .map((d) => ({
          journeyId: d.journey_id ?? '',
          routeId: d.route_id ?? null,
          lineCode: d.line_code ?? 'UNKNOWN',
          status: deriveStatus(d, nowMs),
          expiresMs: toMs(d.expires_at),
          sourceUpdatedMs: toMs(d.source_last_updated),
          schedDepMs:
            toMs(d.origin?.scheduled_departure) ??
            (d.stops && d.stops.length ? toMs(d.stops[0]?.departure?.scheduled) : null),
        }))
        .filter((r) => r.journeyId && isLive(r.schedDepMs, r.expiresMs, nowMs)),
    [docs, nowMs]
  );

  // Sorted client-side, not by a Firestore orderBy: a server-side orderBy silently
  // drops documents missing the field, and the live-window filter has to happen
  // here anyway.
  const columns = useMemo(() => {
    const byLine = new Map<string, Row[]>();
    rows.forEach((r) => {
      const list = byLine.get(r.lineCode) ?? [];
      list.push(r);
      byLine.set(r.lineCode, list);
    });
    return [...byLine.entries()]
      .sort((a, b) => {
        const ka = lineSortKey(a[0]);
        const kb = lineSortKey(b[0]);
        return ka !== kb ? ka - kb : a[0].localeCompare(b[0]);
      })
      .map(([lineCode, list]) => ({
        lineCode,
        rows: list.sort((a, b) => (a.schedDepMs ?? 0) - (b.schedDepMs ?? 0)),
      }));
  }, [rows]);

  // Auto-select once, so the board is not empty on load. Prefers a train actually
  // reported moving over the earliest scheduled one.
  useEffect(() => {
    if (selected || !rows.length) return;
    const pick = rows.find((r) => r.status === 'ACTIVE') ?? rows[0];
    setSelected({ journeyId: pick.journeyId, routeId: pick.routeId, lineCode: pick.lineCode });
  }, [rows, selected, setSelected]);

  return (
    <React.Fragment>
      <Box
        sx={{
          position: 'sticky', top: 0, zIndex: 2,
          bgcolor: '#18364a', pt: '80px', px: '4px', pb: '12px', color: '#98a1aa',
        }}
      >
        <Typography sx={{ fontSize: '12px', marginLeft: '25px' }} noWrap>
          {t('sidebar.title')}:
        </Typography>
        <Typography sx={{ fontSize: '9px', marginLeft: '25px', color: '#5d6679' }} noWrap>
          {t('sidebar.count', { n: rows.length })}
        </Typography>
      </Box>

      {error && (
        <Typography sx={{ fontSize: '10px', color: '#ffb4b4', px: '10px', pb: '8px' }}>
          Firestore: {error}
        </Typography>
      )}

      {!error && rows.length === 0 && (
        <Typography sx={{ fontSize: '10px', color: '#5d6679', px: '10px', pb: '8px' }}>
          {t('sidebar.none')}
        </Typography>
      )}

      <Grid container spacing={0} sx={{ margin: 0 }}>
        {columns.map((col) => (
          <Grid key={col.lineCode} size={4}>
            <Typography sx={{ fontSize: '12px', marginLeft: '15px', color: '#5d6679' }} noWrap>
              {col.lineCode}
            </Typography>

            {col.rows.map((r) => {
              const isSelected = selected?.journeyId === r.journeyId;
              const recentlyWritten =
                r.sourceUpdatedMs !== null && nowMs - r.sourceUpdatedMs < BLINK_MS;
              return (
                <Box
                  key={r.journeyId}
                  sx={{
                    px: '4px', py: '7px',
                    ...(isSelected
                      ? {
                          background: '#0f2a3d',
                          borderLeft: '3px solid #f0a500',
                          borderRadius: '2px',
                          pl: '1px',
                        }
                      : { borderLeft: '3px solid transparent' }),
                  }}
                >
                  <Typography
                    textAlign="center"
                    fontWeight={isSelected || recentlyWritten ? 'bold' : 'normal'}
                    sx={{ fontSize: '12px' }}
                    noWrap
                  >
                    <Link
                      color={recentlyWritten ? '#FFA500' : STATUS_COLOUR[r.status]}
                      underline="none"
                      href="#"
                      title={`${t(`status.${r.status}`)}${r.routeId ? ` · ${r.routeId}` : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setSelected({
                          journeyId: r.journeyId,
                          routeId: r.routeId,
                          lineCode: r.lineCode,
                        });
                      }}
                    >
                      {/* route_id is the operator-facing train number. journey_id
                          is not human readable and must never be the label. */}
                      {r.routeId ?? r.journeyId.slice(-6)}
                    </Link>
                  </Typography>
                </Box>
              );
            })}
          </Grid>
        ))}
      </Grid>

      <Legend />
    </React.Fragment>
  );
};

export default Sidebar2;
